import { useRecoilValue } from 'recoil';
import type { ReplanningStageId } from '~/state/atoms/adaptiveReplanningAtoms';
import { replanningRunOutputsState } from '~/state/atoms/adaptiveReplanningAtoms';
import { isJobRunning, jobQueueState } from '~/state/atoms/jobAtoms';
import { REPLANNING_STAGES } from './replanningStages';
import { useReplanningProgress } from './useReplanningProgress';

export type StageState = 'complete' | 'current' | 'available' | 'blocked';

export interface StageStatus {
    readonly stageId: ReplanningStageId;
    readonly state: StageState;
    readonly reasonId?: string;
}

export interface ReplanningStageStatus {
    readonly stages: readonly StageStatus[];
    readonly isBusy: boolean;
    readonly canContinue: boolean;
    readonly continueReasonId?: string;
    readonly previousStageId?: ReplanningStageId;
    readonly nextStageId?: ReplanningStageId;
}

export function useReplanningStageStatus(
    currentStageId: ReplanningStageId
): ReplanningStageStatus {
    const progress = useReplanningProgress();
    const { propagatedUid, pendingPropagatedUid } = useRecoilValue(
        replanningRunOutputsState
    );
    const { jobs } = useRecoilValue(jobQueueState);

    const isBusy =
        jobs.some((job) => isJobRunning(job.status)) || !!pendingPropagatedUid;

    const index = REPLANNING_STAGES.findIndex(
        (stage) => stage.id === currentStageId
    );
    const currentIndex = index < 0 ? 0 : index;

    const owesNothing = (stageId: ReplanningStageId) => {
        const entry = progress.find(
            (candidate) => candidate.stageId === stageId
        );
        return !!entry && entry.tasks.every((task) => task.done);
    };

    const stages = REPLANNING_STAGES.map((stage, position): StageStatus => {
        if (stage.needsPropagatedSet === true && !propagatedUid) {
            return {
                stageId: stage.id,
                state: 'blocked',
                reasonId: 'replanning.stage.blocked',
            };
        }
        if (position === currentIndex) {
            return { stageId: stage.id, state: 'current' };
        }
        if (position < currentIndex && owesNothing(stage.id)) {
            return { stageId: stage.id, state: 'complete' };
        }
        return { stageId: stage.id, state: 'available' };
    });

    const previous = REPLANNING_STAGES[currentIndex - 1];
    const nextStage = REPLANNING_STAGES[currentIndex + 1];
    const nextStatus = nextStage
        ? stages.find((entry) => entry.stageId === nextStage.id)
        : undefined;

    const continueReasonId = !nextStage
        ? 'replanning.continue.last'
        : isBusy
          ? 'replanning.continue.busy'
          : nextStatus?.state === 'blocked'
            ? nextStatus.reasonId
            : undefined;

    return {
        stages,
        isBusy,
        canContinue: !continueReasonId,
        continueReasonId,
        previousStageId: previous?.id,
        nextStageId: nextStage?.id,
    };
}
